import React, { ReactNode, ReactElement } from 'react';
import { ModalBackdrop, ModalContainer, ModalContent } from 'is24-corecss';
import { css } from '@emotion/css';

import {
  PrimaryFooterButton,
  SecondaryFooterButton,
  FooterButton,
} from 'is24-corecss/build/@types/Modal/ModalFooterButtons';
import { Tabs } from '../tabs/Tabs';
import { GRAY300 } from '../../../../domain/colors';
import { Benefit } from '../../../../@types/benefits';
import { ImageSource, WebPImageElement } from '../imageSource/ImageSource';
import './Modal.css';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

const styles = {
  container: css`
    max-width: 1000px;
    width: 100% !important;
    overflow-x: hidden;
    @media (max-width: 668px) {
      position: fixed !important;
      overflow-y: scroll;
      display: flex;
      flex-flow: column;
      top: 0;
    }
    @media (orientation: landscape) and (max-width: 1023px) {
      height: 100vh;
    },
  `,
  modalContentWrapper: css`
    max-height: inherit !important;
    overflow: hidden !important;
    display: flex;
    flex-flow: column;
    @media (min-width: 668px) {
      max-height: calc(100vh - 200px) !important;
    }
  `,
  modalContent: css`
    overflow-y: auto;
    overflow-x: hidden;
  `,
  imageWrapper: {
    backgroundColor: 'white',
    position: 'relative',
    height: 320,
    margin: '-24px -32px 2px -32px',
    borderBottom: `1px solid ${GRAY300}`,
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
    '@media (max-width: 668px)': {
      margin: '-16px -32px 2px -32px',
    },
  },
  image: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    fit: {
      contain: {
        objectFit: 'contain',
      },
      cover: {
        objectFit: 'cover',
      },
    },
    margin: '0 auto',
    display: 'block',
  },
  contentWrapper: {
    position: 'relative',
    withButton: {
      '@media (max-width: 1013px)': {
        marginBottom: 72,
      },
    },
    '& [class*="tabs-container"]': {
      '@media (max-width: 668px)': {
        marginBottom: 4,
      },
    },
    '& [role="tablist"]': {
      zIndex: 5,
      justifyContent: 'center',
      top: -2,
      padding: '20px 0 24px 0',
      '@media (max-width: 668px)': {
        padding: '20px 0 16px 0',
        marginBottom: -12,
      },
      '@media (min-width: 669px)': {
        position: 'sticky',
      },
    },
  },
  modalBackDrop: css`
    @media (max-width: 669px) {
      position: initial !important;
    }
  `,
} as const;

interface ModalV2Props {
  title: string | ReactNode;
  isVisible: boolean;
  image: string;
  imageWebp?: WebPImageElement;
  imageMobile?: string;
  imageMobileWebp?: WebPImageElement;
  imageUseFullWidth: boolean;
  imageAlt: string;
  imageBackgroundStyles?: object;
  tabsContent: [JSX.Element, JSX.Element?, JSX.Element?, JSX.Element?];
  primaryFooterBtn?: ReactElement<typeof PrimaryFooterButton | typeof FooterButton>;
  secondaryFooterBtn?: ReactElement<typeof SecondaryFooterButton | typeof FooterButton>;
  formData?: JSX.Element | null;
  benefit: Benefit;
  onClose: () => void;
}

const ModalV2 = ({
  title,
  isVisible,
  image,
  imageWebp,
  imageMobile,
  imageMobileWebp,
  imageUseFullWidth,
  imageAlt,
  imageBackgroundStyles,
  tabsContent,
  benefit,
  formData,
  primaryFooterBtn,
  secondaryFooterBtn,
  onClose,
}: ModalV2Props) => {
  const camelCasedContentIdentifier = `_${benefit.toLowerCase()}ModalContent`.replace(/_./g, (match) =>
    match[1].toUpperCase(),
  );
  useElementDarkMode('[data-testid="modal-backdrop"]', isVisible);
  useBodyScroll(isVisible);
  useModalFocus(isVisible);

  return (
    <ModalBackdrop visible={isVisible} onClose={onClose} className={css(styles.modalBackDrop)}>
      <ModalContainer
        containerClassName={`ModalV2 ${css(styles.container)} ${camelCasedContentIdentifier}`}
        contentWrapperClassName={`absolute-reference ${css(styles.modalContentWrapper)}`}
        visible={isVisible}
        modalTitle={title}
        onClose={onClose}
        primaryFooterBtn={primaryFooterBtn}
        secondaryFooterBtn={secondaryFooterBtn}
      >
        <ModalContent className={`${css(styles.modalContent)}`}>
          <div>
            {!formData && (
              <div data-testid='ImageWrapper' className={css({ ...styles.imageWrapper, ...imageBackgroundStyles })}>
                <ImageSource
                  image={image}
                  imageWebp={imageWebp}
                  imageMobile={imageMobile}
                  imageMobileWebp={imageMobileWebp}
                  imageAlt={imageAlt}
                  sizes='(min-width: 1014px) 1000px, 100vw'
                  style={{
                    ...(imageUseFullWidth ? styles.image.fit.cover : styles.image.fit.contain),
                    ...styles.image,
                  }}
                />
              </div>
            )}
            <div
              className={`${css(styles.contentWrapper, (primaryFooterBtn || secondaryFooterBtn) && styles.contentWrapper.withButton)}`}
            >
              {formData ? formData : <Tabs tabsContent={tabsContent} benefit={benefit} />}
            </div>
          </div>
        </ModalContent>
      </ModalContainer>
    </ModalBackdrop>
  );
};

export default ModalV2;
